import { useEffect, useMemo, useState } from "react";

import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import { ReactComponent as WhiteLogo } from "../../assets/logo/white-logo.svg";
import {
  attributionEventParams,
  captureAttribution,
} from "../apply/attribution";
import {
  ensureClickIdCookie,
  initFacebookPixel,
  trackLeadOnce,
} from "../apply/facebookPixel";
import "../influencers-home/styles.css";
import "./styles.css";

const REDIRECT_SECONDS = 5;
const WHATSAPP_PHONE = "5511919829965";
const WHATSAPP_MESSAGE =
  "Olá! Acabei de enviar minha candidatura para o Ginastee e quero falar com o time sobre a minha comunidade.";

const PAGE_TITLE = "Candidatura enviada | Ginastee";

function buildWhatsAppUrl() {
  const params = new URLSearchParams({
    phone: WHATSAPP_PHONE,
    text: WHATSAPP_MESSAGE,
    type: "phone_number",
    app_absent: "0",
  });
  return `https://api.whatsapp.com/send/?${params.toString()}`;
}

function usePageMetadata() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    /* Página de conversão não deve ser indexada. */
    const robotsTag = document.querySelector('meta[name="robots"]');
    const previousRobots = robotsTag?.getAttribute("content");
    robotsTag?.setAttribute("content", "noindex, nofollow");

    return () => {
      document.title = previousTitle;
      if (previousRobots != null) {
        robotsTag?.setAttribute("content", previousRobots);
      }
    };
  }, []);
}

export default function ApplyThanks() {
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);
  const whatsappUrl = useMemo(buildWhatsAppUrl, []);

  usePageMetadata();

  useEffect(() => {
    /* A jornada passou pelo ActiveCampaign, então recuperamos o clique guardado
       em /apply antes de reportar a conversão. Os cookies _fbp/_fbc continuam
       válidos por serem do mesmo domínio. */
    const attribution = captureAttribution();
    ensureClickIdCookie(attribution.fbclid, attribution.capturedAt);

    initFacebookPixel();
    trackLeadOnce({
      content_name: "Candidatura creator",
      content_category: "apply",
      ...attributionEventParams(attribution),
    });
  }, []);

  useEffect(() => {
    const countdown = window.setInterval(() => {
      setSecondsLeft((current) => (current > 0 ? current - 1 : 0));
    }, 1000);

    const redirect = window.setTimeout(() => {
      window.location.replace(whatsappUrl);
    }, REDIRECT_SECONDS * 1000);

    return () => {
      window.clearInterval(countdown);
      window.clearTimeout(redirect);
    };
  }, [whatsappUrl]);

  return (
    <main className="creator-home apply-thanks-page" id="top">
      <div className="thanks-glow" aria-hidden="true" />

      <div className="thanks-shell">
        <a className="thanks-mark" href="/apply" aria-label="Ginastee">
          <WhiteLogo aria-hidden="true" />
        </a>

        <section className="thanks-card">
          <span className="thanks-badge" aria-hidden="true">
            <CheckRoundedIcon />
          </span>

          <span className="micro-label">Candidatura enviada</span>
          <h1>Recebemos a sua candidatura.</h1>
          <p>
            Nosso time vai analisar seu perfil, sua audiência e o método que
            você quer transformar em produto.
          </p>

          <div className="thanks-next">
            <strong>Próximo passo</strong>
            <p>
              Vamos te levar agora para uma conversa direta com o nosso time no
              WhatsApp. Assim conseguimos acelerar a análise do seu perfil.
            </p>
          </div>

          <div className="thanks-countdown">
            <span aria-live="polite">
              Redirecionando para o WhatsApp em <b>{secondsLeft}s</b>
            </span>
            <i
              className="thanks-progress"
              style={{ "--countdown-duration": `${REDIRECT_SECONDS}s` }}
              aria-hidden="true"
            />
          </div>

          <a className="action-link action-light" href={whatsappUrl}>
            <span>Falar agora no WhatsApp</span>
            <WhatsAppIcon aria-hidden="true" />
          </a>

          <small>
            Se o redirecionamento não acontecer, toque no botão acima.
          </small>
        </section>
      </div>
    </main>
  );
}
